import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthStore from '@/store/authStore';
import { compressImage } from '@/utils/imageCompression';
import { getMyProfile, patchMyProfile, profileError, uploadProfilePhoto, type MyProfile, type ProfilePatch } from './api';

/*
 * Profile (Ray 2026-10-25): header menu → Profile. Cover photo, avatar
 * (distributors and SmartFi staff only), first/last name and bio.
 * No Save button: every field saves on change/blur and says "Saved ✓";
 * photos save as soon as their upload finishes.
 */

export const PROFILE_KEY = ['profile', 'me'] as const;
const BIO_MAX = 160;
const PHOTO_TYPES = 'image/jpeg,image/png,image/webp';

/** My profile (also feeds the header's name and picture). */
export function useMyProfile() {
   const isAuthed = useAuthStore((s) => s.isAuthenticated);
   return useQuery({ queryKey: PROFILE_KEY, queryFn: getMyProfile, enabled: isAuthed, staleTime: 5 * 60_000, retry: false });
}

/** After a save: the modal, the header name/picture and the stored sign-in all show the new values. */
function useApplyProfile() {
   const qc = useQueryClient();
   return (p: MyProfile) => {
      qc.setQueryData(PROFILE_KEY, p);
      const { token, user, setAuth } = useAuthStore.getState();
      if (token && user && (user.name !== p.name || user.avatar !== p.avatar)) setAuth(token, { ...user, name: p.name || user.name, avatar: p.avatar });
   };
}

type Field = 'firstName' | 'lastName' | 'bio' | 'facebookUrl';
const FIELDS: Field[] = ['firstName', 'lastName', 'bio', 'facebookUrl'];

export default function ProfileModal({ onClose }: { onClose: () => void }) {
   const { data: profile, error: loadError } = useMyProfile();
   const apply = useApplyProfile();
   const [draft, setDraft] = useState<Record<Field, string> | null>(null);
   const [saved, setSaved] = useState<Partial<Record<Field | 'avatar' | 'cover', boolean>>>({});
   const [errors, setErrors] = useState<Partial<Record<Field | 'avatar' | 'cover', string>>>({});
   const [busy, setBusy] = useState<'avatar' | 'cover' | null>(null);
   const coverInput = useRef<HTMLInputElement>(null);
   const avatarInput = useRef<HTMLInputElement>(null);
   const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

   useEffect(() => {
      if (profile && !draft) setDraft({ firstName: profile.firstName, lastName: profile.lastName, bio: profile.bio, facebookUrl: profile.facebookUrl ?? '' });
   }, [profile, draft]);
   // Typed but not saved yet (focus still in the field): Esc / a click outside must not throw it away.
   const dirty = !!profile && !!draft && FIELDS.some((k) => (k === 'bio' ? draft[k] : draft[k].trim()) !== (profile[k] ?? ''));
   const dirtyRef = useRef(dirty);
   dirtyRef.current = dirty;
   useEffect(() => {
      const esc = (e: KeyboardEvent) => e.key === 'Escape' && !dirtyRef.current && onClose();
      document.addEventListener('keydown', esc);
      return () => document.removeEventListener('keydown', esc);
   }, [onClose]);
   useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

   const flashSaved = (k: Field | 'avatar' | 'cover') => {
      setSaved((s) => ({ ...s, [k]: true }));
      clearTimeout(timers.current[k]);
      timers.current[k] = setTimeout(() => setSaved((s) => ({ ...s, [k]: false })), 2500);
   };
   const save = async (k: Field | 'avatar' | 'cover', patch: ProfilePatch) => {
      setErrors((e) => ({ ...e, [k]: undefined }));
      try {
         const next = await patchMyProfile(patch);
         apply(next);
         // The server tidies the link (facebook.com/x → https://www.facebook.com/x): show what was saved.
         if (k === 'facebookUrl') setDraft((d) => (d ? { ...d, facebookUrl: next.facebookUrl ?? '' } : d));
         flashSaved(k);
      } catch (e) {
         setErrors((x) => ({ ...x, [k]: profileError(e) }));
      }
   };
   const saveField = (k: Field) => {
      if (!profile || !draft) return;
      const v = k === 'bio' ? draft[k] : draft[k].trim();
      if (v === (profile[k] ?? '')) return;
      void save(k, { [k]: v });
   };
   const upload = async (which: 'avatar' | 'cover', f: File | undefined) => {
      if (!f) return;
      if (!PHOTO_TYPES.split(',').includes(f.type)) return setErrors((e) => ({ ...e, [which]: 'Use a JPG, PNG or WebP picture.' }));
      setErrors((e) => ({ ...e, [which]: undefined }));
      setBusy(which);
      try {
         // Cover ~1500px wide, avatar 512px — WebP, so it loads fast everywhere.
         const small = (await compressImage(f, { maxEdge: which === 'cover' ? 1500 : 512, quality: 0.82 })).file;
         apply(await uploadProfilePhoto(which, small));
         flashSaved(which);
      } catch (e) {
         setErrors((x) => ({ ...x, [which]: e instanceof Error && !(e as any).response ? e.message : profileError(e, 'The upload failed. Try again.') }));
      } finally {
         setBusy(null);
      }
   };

   const initial = (profile?.firstName || profile?.name || '?').charAt(0).toUpperCase();

   return createPortal(
      <Scrim data-strict={dirty ? 'true' : undefined} onMouseDown={(e) => e.target === e.currentTarget && !dirtyRef.current && onClose()}>
         <Card role="dialog" aria-label="Profile">
            <Head>
               <h3>Profile</h3>
               <CloseBtn type="button" aria-label="Close" title="Close (Esc)" onClick={onClose}>
                  ✕
               </CloseBtn>
            </Head>
            {!profile ? (
               <Muted>{loadError ? profileError(loadError, 'Could not load your profile.') : 'Loading…'}</Muted>
            ) : (
               <>
                  <Cover
                     type="button"
                     $url={profile.cover}
                     aria-label="Change cover photo"
                     title="Change cover photo"
                     disabled={busy === 'cover'}
                     onClick={() => coverInput.current?.click()}
                  >
                     <CoverHint>{busy === 'cover' ? 'Uploading…' : profile.cover ? '📷 Change cover' : '📷 Add a cover photo'}</CoverHint>
                  </Cover>
                  <input ref={coverInput} type="file" accept={PHOTO_TYPES} hidden data-testid="cover-input" onChange={(e) => {
                     void upload('cover', e.target.files?.[0]);
                     e.target.value = '';
                  }} />
                  <AvatarRow>
                     <Avatar $locked={!profile.canChangeAvatar}>
                        {profile.avatar ? <img src={profile.avatar} alt="" /> : <span>{initial}</span>}
                        {profile.canChangeAvatar && (
                           <AvatarBtn type="button" aria-label="Change profile photo" title="Change profile photo" disabled={busy === 'avatar'} onClick={() => avatarInput.current?.click()}>
                              {busy === 'avatar' ? '…' : '📷'}
                           </AvatarBtn>
                        )}
                     </Avatar>
                     <AvatarSide>
                        {profile.cover && (
                           <LinkBtn type="button" onClick={() => void save('cover', { cover: null })}>
                              Remove cover
                           </LinkBtn>
                        )}
                        {!profile.canChangeAvatar && <Note>🔒 Only distributors can change their photo.</Note>}
                        <Status k="cover" saved={saved} errors={errors} label="Cover" />
                        <Status k="avatar" saved={saved} errors={errors} label="Photo" />
                     </AvatarSide>
                  </AvatarRow>
                  {profile.canChangeAvatar && (
                     <input ref={avatarInput} type="file" accept={PHOTO_TYPES} hidden data-testid="avatar-input" onChange={(e) => {
                        void upload('avatar', e.target.files?.[0]);
                        e.target.value = '';
                     }} />
                  )}
                  {draft && (
                     <Fields>
                        <Two>
                           <Label>
                              <span>
                                 First name <Status k="firstName" saved={saved} errors={errors} />
                              </span>
                              <input
                                 value={draft.firstName}
                                 maxLength={40}
                                 onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                                 onBlur={() => saveField('firstName')}
                                 onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                              />
                           </Label>
                           <Label>
                              <span>
                                 Last name <Status k="lastName" saved={saved} errors={errors} />
                              </span>
                              <input
                                 value={draft.lastName}
                                 maxLength={40}
                                 onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
                                 onBlur={() => saveField('lastName')}
                                 onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                              />
                           </Label>
                        </Two>
                        <Label>
                           <span>
                              Bio <Status k="bio" saved={saved} errors={errors} />
                              <Count>
                                 {draft.bio.length}/{BIO_MAX}
                              </Count>
                           </span>
                           <textarea
                              rows={2}
                              value={draft.bio}
                              maxLength={BIO_MAX}
                              placeholder="e.g. Piso WiFi in Batangas since 2021 — Ruijie and MikroTik"
                              onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
                              onBlur={() => saveField('bio')}
                           />
                        </Label>
                        <Label>
                           <span>
                              Facebook profile <Status k="facebookUrl" saved={saved} errors={errors} />
                           </span>
                           <input
                              value={draft.facebookUrl}
                              maxLength={300}
                              placeholder="e.g. facebook.com/juan.delacruz — shown on your chat card"
                              onChange={(e) => setDraft({ ...draft, facebookUrl: e.target.value })}
                              onBlur={() => saveField('facebookUrl')}
                              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                           />
                        </Label>
                        {FIELDS.map((k) => errors[k] && <Err key={k}>{errors[k]}</Err>)}
                     </Fields>
                  )}
               </>
            )}
         </Card>
      </Scrim>,
      document.body
   );
}

function Status({
   k,
   saved,
   errors,
   label,
}: {
   k: Field | 'avatar' | 'cover';
   saved: Partial<Record<string, boolean>>;
   errors: Partial<Record<string, string>>;
   label?: string;
}) {
   if (label && errors[k]) return <Err>{errors[k]}</Err>;
   if (!saved[k]) return null;
   return <Saved role="status">{label ? `${label} saved ✓` : 'Saved ✓'}</Saved>;
}

/** The header menu's "Profile" item and the modal it opens. */
export function useProfileMenu() {
   const [open, setOpen] = useState(false);
   return {
      action: { icon: 'user-line', label: 'Profile', onClick: () => setOpen(true) },
      modal: open ? <ProfileModal onClose={() => setOpen(false)} /> : null,
   };
}

const Scrim = styled.div`
   position: fixed;
   inset: 0;
   z-index: 1200;
   display: grid;
   place-items: center;
   padding: 16px;
   background: rgba(10, 14, 40, 0.45);
`;
const Card = styled.div`
   width: min(460px, 100%);
   max-height: calc(100vh - 32px);
   overflow: auto;
   padding: 24px;
   border-radius: 16px;
   background: var(--app-surface, #fff);
   color: var(--app-text, #1d1d1f);
   border: 1px solid var(--app-border, #ebebeb);
   box-shadow: 0 24px 60px var(--app-shadow, rgba(0, 0, 0, 0.2));
`;
const Head = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 12px;
   h3 {
      margin: 0;
      font-size: 17px;
      font-weight: 800;
   }
`;
const CloseBtn = styled.button`
   width: 32px;
   height: 32px;
   border-radius: 8px;
   border: 1px solid var(--app-border, #ebebeb);
   background: var(--app-surface-muted, #f5f5f5);
   color: var(--app-text-dim, #434346);
   cursor: pointer;
`;
const Cover = styled.button<{ $url: string | null }>`
   position: relative;
   display: block;
   width: 100%;
   height: 120px;
   border: 0;
   border-radius: 12px;
   cursor: pointer;
   background: ${({ $url }) => ($url ? `center / cover no-repeat url("${$url}")` : 'linear-gradient(120deg, #3760f9, #6a4dff)')};
`;
const CoverHint = styled.span`
   position: absolute;
   right: 8px;
   bottom: 8px;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 600;
   color: #fff;
   background: rgba(0, 0, 0, 0.5);
`;
const AvatarRow = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 12px;
   margin: -34px 0 12px 14px;
`;
const Avatar = styled.div<{ $locked: boolean }>`
   position: relative;
   flex-shrink: 0;
   width: 76px;
   height: 76px;
   border-radius: 50%;
   border: 4px solid var(--app-surface, #fff);
   background: linear-gradient(135deg, #3760f9, #6a4dff);
   display: grid;
   place-items: center;
   color: #fff;
   font-size: 28px;
   font-weight: 800;
   box-sizing: border-box;
   /* Pinned inside the circle: a tall photo used to stretch it into an oval over "First name" (Ray 2026-10-27). */
   img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
   }
`;
const AvatarBtn = styled.button`
   position: absolute;
   right: -4px;
   bottom: -4px;
   width: 30px;
   height: 30px;
   border-radius: 50%;
   border: 2px solid var(--app-surface, #fff);
   background: var(--app-surface-muted, #f5f5f5);
   cursor: pointer;
   font-size: 14px;
`;
const AvatarSide = styled.div`
   display: grid;
   gap: 2px;
   padding-bottom: 4px;
   min-width: 0;
`;
const Note = styled.small`
   color: var(--app-text-muted, #7a7a7a);
   font-size: 12px;
`;
const LinkBtn = styled.button`
   justify-self: start;
   padding: 0;
   border: 0;
   background: none;
   color: var(--app-text-dim, #434346);
   font-size: 12px;
   text-decoration: underline;
   cursor: pointer;
`;
const Fields = styled.div`
   display: grid;
   gap: 10px;
`;
const Two = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
   @media (max-width: 420px) {
      grid-template-columns: 1fr;
   }
`;
const Label = styled.label`
   display: grid;
   gap: 4px;
   font-size: 12px;
   font-weight: 600;
   color: var(--app-text-dim, #434346);
   > span {
      display: flex;
      align-items: baseline;
      gap: 8px;
   }
   input,
   textarea {
      width: 100%;
      padding: 9px 11px;
      border-radius: 10px;
      border: 1px solid var(--app-border-strong, #ccc);
      background: var(--app-surface, #fff);
      color: var(--app-text, #1d1d1f);
      font: inherit;
      font-size: 14px;
      font-weight: 400;
      resize: vertical;
   }
`;
const Count = styled.small`
   margin-left: auto;
   font-weight: 400;
   color: var(--app-text-soft, #a3a3a3);
`;
const Saved = styled.small`
   color: #198754;
   font-weight: 600;
`;
const Err = styled.small`
   color: #dc3545;
   font-weight: 600;
`;
const Muted = styled.p`
   color: var(--app-text-muted, #7a7a7a);
`;
